import { useEffect, useMemo } from "react";

import { useLocation, useParams } from "react-router";

import { CheckStatus } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { useChecksRedirect } from "flows/Inventory/shared/hooks/useChecksRedirect";
import { serializeAndTranslateCheck } from "flows/Inventory/shared/models/check/serializer";
import { useGetCheckByIdQuery } from "flows/Inventory/shared/queries/check/check.generated";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { isNullOrUndefined } from "utils/tsHelpers";

import { useBbdCheckStore } from "../stores/useBbdCheckStore";

/**
 * Handles fetching check data and initializing the Zustand store.
 * Pages should use `useBbdCheckStore()` directly for reading state and actions.
 * This hook only needs to be called once per page to ensure the store is initialized.
 */
export function useBbdCheckService() {
  const { checkId: urlCheckId } = useParams<{ checkId: string }>();
  const { state: locationState } = useLocation() as {
    state: { origin: PageName } | null;
  };
  const origin = locationState?.origin as PageName;

  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { sendSegmentTrackEvent } = useAnalytics();
  const { navigateToOrigin } = useChecksRedirect();

  const storeCheckId = useBbdCheckStore((s) => s.checkId);
  const initializeCheck = useBbdCheckStore((s) => s.initializeCheck);

  // Only fetch if store is not yet initialized for this checkId
  const needsFetch = storeCheckId !== urlCheckId;

  const { data, loading } = useGetCheckByIdQuery({
    skip: isNullOrUndefined(urlCheckId) || !needsFetch,
    variables: { checkId: urlCheckId!, isAlwaysInbound: true },
    fetchPolicy: "network-only",
  });

  const taskData = useMemo(() => {
    if (!data?.getTaskById?.task) {
      return null;
    }
    return serializeAndTranslateCheck(data.getTaskById.task, appLanguage);
  }, [data, appLanguage]);

  const isInvalidTask = !needsFetch
    ? false
    : !loading &&
      (isNullOrUndefined(data) ||
        isNullOrUndefined(data?.getTaskById?.task?.product?.sku) ||
        isNullOrUndefined(data?.getTaskById?.task?.priority) ||
        data?.getTaskById?.task?.status === CheckStatus.done);

  const isInvalidParams = isNullOrUndefined(urlCheckId) || isInvalidTask;

  // Redirect on invalid params
  useEffect(() => {
    if (isInvalidParams && !loading) {
      navigateToOrigin(origin);
    }
  }, [isInvalidParams, loading, navigateToOrigin, origin]);

  // Initialize the store when data is ready
  useEffect(() => {
    if (!loading && !isInvalidParams && urlCheckId && needsFetch && taskData) {
      initializeCheck({
        checkId: urlCheckId,
        origin: origin ?? PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
        shelfNumber: taskData.shelfNumber ?? null,
        shelfLetter: taskData.shelfLetter ?? null,
        priority: taskData.priority ?? 0,
        sku: taskData.sku ?? null,
        productName: taskData.productName ?? null,
        productImageUrl: taskData.productImageUrl ?? null,
        productCountryOfOriginName: taskData.productCountryOfOriginName ?? null,
        minDaysToBestBeforeDate: taskData.minDaysToBestBeforeDate ?? null,
      });
    }
  }, [loading, isInvalidParams, urlCheckId, needsFetch, initializeCheck, origin, taskData]);

  // Track when bbd check is started (only on first page load)
  useEffect(() => {
    if (urlCheckId && !isInvalidParams && needsFetch && taskData?.sku) {
      sendSegmentTrackEvent("stockCheckStarted", {
        type: "automated",
        check_id: urlCheckId,
        product_sku: taskData.sku,
        origin: origin ?? PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
      });
    }
  }, [urlCheckId, isInvalidParams, needsFetch, origin, sendSegmentTrackEvent, taskData?.sku]);

  return {
    loading: needsFetch && loading,
    isInvalidParams,
  };
}
